import React from 'react';
import { 
  FileText, 
  Search, 
  Filter, 
  Download,
  User,
  Clock,
  Shield,
  Eye,
  Edit,
  Trash2,
  Plus,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

const auditLogs = [
  {
    id: 'AUD-001',
    timestamp: '2024-01-15 14:32:15',
    user: 'John Smith',
    role: 'Manager',
    action: 'CREATE',
    module: 'POS',
    resource: 'Order #1247',
    details: 'Created new order for Table T3',
    ip: '192.168.1.45',
  },
  {
    id: 'AUD-002',
    timestamp: '2024-01-15 14:28:42',
    user: 'Sarah Johnson',
    role: 'Staff',
    action: 'UPDATE',
    module: 'Inventory',
    resource: 'Stock Level',
    details: 'Updated chicken tikka stock: 10kg → 2.5kg',
    ip: '192.168.1.48',
  },
  {
    id: 'AUD-003',
    timestamp: '2024-01-15 14:15:00',
    user: 'Admin User',
    role: 'Admin',
    action: 'UPDATE',
    module: 'Settings',
    resource: 'Tax Rate',
    details: 'Modified VAT rate from 20% to 20%',
    ip: '192.168.1.1',
  },
  {
    id: 'AUD-004',
    timestamp: '2024-01-15 13:45:22',
    user: 'Mike Wilson',
    role: 'Manager',
    action: 'DELETE',
    module: 'Menu',
    resource: 'Menu Item',
    details: 'Removed "Seasonal Special" from menu',
    ip: '192.168.1.45',
  },
  {
    id: 'AUD-005',
    timestamp: '2024-01-15 13:30:00',
    user: 'System',
    role: 'System',
    action: 'LOGIN',
    module: 'Auth',
    resource: 'User Session',
    details: 'User john.smith@isfco.com logged in',
    ip: '192.168.1.45',
  },
  {
    id: 'AUD-006',
    timestamp: '2024-01-15 12:15:30',
    user: 'Sarah Johnson',
    role: 'Staff',
    action: 'CREATE',
    module: 'Reservations',
    resource: 'Booking #RES-089',
    details: 'Created reservation for 6 guests at 7:30 PM',
    ip: '192.168.1.48',
  },
  {
    id: 'AUD-007',
    timestamp: '2024-01-15 11:45:00',
    user: 'Admin User',
    role: 'Admin',
    action: 'UPDATE',
    module: 'Users',
    resource: 'User Permissions',
    details: 'Updated role permissions for Staff group',
    ip: '192.168.1.1',
  },
];

const actionConfig = {
  CREATE: { color: 'text-spice-cardamom', bg: 'bg-spice-cardamom/10' },
  UPDATE: { color: 'text-brand-orange', bg: 'bg-brand-orange/10' },
  DELETE: { color: 'text-spice-tandoori', bg: 'bg-spice-tandoori/10' },
  LOGIN: { color: 'text-blue-500', bg: 'bg-blue-50' },
  LOGOUT: { color: 'text-gray-500', bg: 'bg-gray-100' },
};

export function AuditLog() {
  return (
    <div data-testid="audit-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-brand-charcoal">Audit Log</h1>
          <p className="text-gray-500 text-sm mt-1">Track all system activities and changes</p>
        </div>
        <Button data-testid="export-audit-btn" variant="outline" className="gap-2">
          <Download className="w-4 h-4" />
          Export Logs
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brand-orange/10 rounded-lg">
                <FileText className="w-5 h-5 text-brand-orange" />
              </div>
              <div>
                <p className="text-2xl font-semibold font-mono">1,247</p>
                <p className="text-sm text-gray-500">Total Events (30d)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-spice-cardamom/10 rounded-lg">
                <Plus className="w-5 h-5 text-spice-cardamom" />
              </div>
              <div>
                <p className="text-2xl font-semibold font-mono">423</p>
                <p className="text-sm text-gray-500">Create Actions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brand-orange/10 rounded-lg">
                <Edit className="w-5 h-5 text-brand-orange" />
              </div>
              <div>
                <p className="text-2xl font-semibold font-mono">612</p>
                <p className="text-sm text-gray-500">Update Actions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-spice-tandoori/10 rounded-lg">
                <Trash2 className="w-5 h-5 text-spice-tandoori" />
              </div>
              <div>
                <p className="text-2xl font-semibold font-mono">18</p>
                <p className="text-sm text-gray-500">Delete Actions</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input 
                data-testid="audit-search"
                placeholder="Search logs..." 
                className="pl-9" 
              />
            </div>
            <Select>
              <SelectTrigger data-testid="filter-action" className="w-[140px]">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
                <SelectItem value="login">Login</SelectItem>
              </SelectContent>
            </Select>
            <Select>
              <SelectTrigger data-testid="filter-module" className="w-[140px]">
                <SelectValue placeholder="Module" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                <SelectItem value="pos">POS</SelectItem>
                <SelectItem value="inventory">Inventory</SelectItem>
                <SelectItem value="finance">Finance</SelectItem>
                <SelectItem value="settings">Settings</SelectItem>
              </SelectContent>
            </Select>
            <Select>
              <SelectTrigger data-testid="filter-user" className="w-[140px]">
                <SelectValue placeholder="User" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="manager">Managers</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" className="gap-2">
              <Filter className="w-4 h-4" />
              More Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Audit Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-[120px]">ID</TableHead>
                <TableHead className="w-[160px]">Timestamp</TableHead>
                <TableHead>User</TableHead>
                <TableHead className="w-[100px]">Action</TableHead>
                <TableHead>Module</TableHead>
                <TableHead>Details</TableHead>
                <TableHead className="w-[120px]">IP Address</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {auditLogs.map((log) => {
                const config = actionConfig[log.action];
                
                return (
                  <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                    <TableCell className="font-mono text-sm text-gray-500">
                      {log.id}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {log.timestamp}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-gray-500" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{log.user}</p>
                          <p className="text-xs text-gray-500">{log.role}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={cn(config.bg, config.color, 'border-0')}>
                        {log.action}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{log.module}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[300px]">
                      <p className="text-sm truncate">{log.details}</p>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-gray-500">
                      {log.ip}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-gray-500">
              Showing 1-7 of 1,247 entries
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="gap-1">
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <Button variant="outline" size="sm" className="w-8 bg-brand-orange text-white hover:bg-brand-orange/90">
                1
              </Button>
              <Button variant="outline" size="sm" className="w-8">2</Button>
              <Button variant="outline" size="sm" className="w-8">3</Button>
              <span className="text-gray-400">...</span>
              <Button variant="outline" size="sm" className="w-8">178</Button>
              <Button variant="outline" size="sm" className="gap-1">
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
